// React
import { useState, useContext, useMemo } from "react";
import { useQuery, gql, useMutation } from "@apollo/client";

// Components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import Footer from "components/Footer";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import SoftButton from "components/SoftButton";
import ModalDelete from "components/Modals/Delete";
import { Card, Grid, Icon, Tooltip, Menu, MenuItem } from "@mui/material";

// Data
import eventosData from "./data/eventosData";
import Loading from "components/Carteles/Loading";
import DataTable from "components/Tables/DataTable";
import SinDatos from "components/Carteles/SinDatos";
import CustomPagination from "components/Pagination";
import { getUproColor } from "utils/colors";
import SoftInput from "components/SoftInput";
import { MessageManager } from "context";
import { UserContext } from "context/user";
import ClientesEvento from "./components/clientesEvento";
import ModalAddEvento from "./components/modalAdd";
import DetallesEvento from "./components/detallesEventos";

function Eventos() {
  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [openMenu, setOpenMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openModalAdd, setOpenModalAdd] = useState(false);
  const [openClientesAdmin, setOpenClientesAdmin] = useState(false);
  const [openModalDetalles, setOpenModalDetalles] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [estadoMenuAnchor, setEstadoMenuAnchor] = useState(null);
  const [page, setPage] = useState(1);
  const [pagesInfo, setPagesInfo] = useState({
    pages: 0,
    total: 0,
  });
  const [orderBy, setOrderBy] = useState({
    campo: "id",
    orden: "DESC",
  });
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const { handleSnackbar } = useContext(MessageManager);
  const { columns, getRows } = eventosData;
  const handleOrderBy = (campo) => {
    if (orderBy.campo === campo) {
      if (orderBy.orden === "ASC") {
        setOrderBy({
          campo: campo,
          orden: "DESC",
        });
      } else {
        setOrderBy({
          campo: campo,
          orden: "ASC",
        });
      }
    } else {
      setOrderBy({
        campo: campo,
        orden: "ASC",
      });
    }
  };

  const handleOpenMenu = (event, item) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(true);
    setSelectedItem(item);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setOpenMenu(false);
    setEstadoMenuAnchor(null);
  };

  const handleSearch = (event) => {
    const newTimeout = setTimeout(() => {
      setSearch(event.target.value);
      setPage(1);
    }, 500);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(newTimeout);
  };

  const { loading, data, refetch } = useQuery(
    gql`
      query GetEventos($search: String, $page: Int, $order: Order) {
        paginationInfo {
          pages
          total
        }
        eventos(search: $search, pagination: { page: $page, limit: 10 }, order: $order) {
          id
          clienteEvento {
            id
            nombre
            razonSocial
          }
          fechaEvento
          estado
          nombreEvento
        }
      }
    `,
    {
      variables: {
        search: search || null,
        page: page,
        order: { field: orderBy.campo, order: orderBy.orden },
      },
      fetchPolicy: "network-only",
    }
  );

  const [updateEventoEstado] = useMutation(
    gql`
      mutation updateEventoEstado($input: EventoInput!) {
        saveEvento(input: $input) {
          id
          estado
        }
      }
    `
  );

  const handleOpenEstadoSubmenu = (e) => setEstadoMenuAnchor(e.currentTarget);
  const handleChangeEstadoDirect = async (estado) => {
    const ev = selectedItem;
    if (!ev) return handleCloseMenu();
    try {
      await updateEventoEstado({ variables: { input: { id: ev.id, estado } } });
      handleSnackbar("Estado del evento actualizado", "success");
      if (typeof refetch === "function") refetch();
    } catch (e) {
      handleSnackbar("Error al actualizar estado", "error");
    } finally {
      handleCloseMenu();
    }
  };

  const renderMenu = (
    <Menu id="simple-menu" anchorEl={anchorEl} open={Boolean(openMenu)} onClose={handleCloseMenu}>
      <MenuItem
        onClick={() => {
          setOpenModalDetalles(true);
          handleCloseMenu();
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: "100%",
          }}
        >
          <SoftBox>
            <SoftTypography variant="p">Ver detalles</SoftTypography>
          </SoftBox>
          <SoftBox>
            <Icon
              fontSize="small"
              sx={{
                verticalAlign: "middle",
                color: getUproColor("uproGreen"),
              }}
            >
              visibility
            </Icon>
          </SoftBox>
        </SoftBox>
      </MenuItem>
      <MenuItem onClick={handleOpenEstadoSubmenu}>
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <SoftBox>
            <SoftTypography variant="p">Cambiar estado</SoftTypography>
          </SoftBox>
          <SoftBox>
            <Icon fontSize="small" sx={{ verticalAlign: "middle", color: getUproColor("uproBlue") }}>flag</Icon>
          </SoftBox>
        </SoftBox>
      </MenuItem>
      <Menu
        anchorEl={estadoMenuAnchor}
        open={Boolean(estadoMenuAnchor)}
        onClose={() => setEstadoMenuAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem onClick={() => handleChangeEstadoDirect("Pendiente")}>
          <Icon sx={{ mr: 1, color: getUproColor("uproOrange") }}>schedule</Icon>
          Pendiente
        </MenuItem>
        <MenuItem onClick={() => handleChangeEstadoDirect("Cancelado")}>
          <Icon sx={{ mr: 1, color: getUproColor("error") }}>cancel</Icon>
          Cancelado
        </MenuItem>
        <MenuItem onClick={() => handleChangeEstadoDirect("Finalizado")}>
          <Icon sx={{ mr: 1, color: getUproColor("uproGreen") }}>task_alt</Icon>
          Finalizado
        </MenuItem>
      </Menu>
      <MenuItem
        onClick={() => {
          setOpenModalDelete(true);
          handleCloseMenu();
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: "100%",
          }}
        >
          <SoftBox>
            <SoftTypography variant="p">Eliminar</SoftTypography>
          </SoftBox>
          <SoftBox>
            <Icon
              fontSize="small"
              sx={{
                verticalAlign: "middle",
                color: getUproColor("uproPink"),
              }}
            >
              delete
            </Icon>
          </SoftBox>
        </SoftBox>
      </MenuItem>
    </Menu>
  );

  const allRows = useMemo(() => {
    if (data) {
      setPagesInfo(data.paginationInfo);
      return getRows(data.eventos, handleOpenMenu);
    }
  }, [data]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Card>
            <Grid container spacing={2} p={3}>
              <Grid item xs={12}>
                <SoftBox
                  display={{
                    xs: "flex-row",
                    sm: "flex",
                  }}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                    <SoftTypography variant="h6">Eventos</SoftTypography>
                    <SoftBox
                      display={{
                        xs: "block",
                        sm: "none",
                      }}
                    >
                      <Tooltip title="Agregar nuevo evento" placement="top">
                        <SoftButton
                          color="primary"
                          circular
                          iconOnly
                          onClick={() => setOpenModalAdd(true)}
                        >
                          <Icon>add</Icon>
                        </SoftButton>
                      </Tooltip>
                    </SoftBox>
                  </SoftBox>
                  <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="end"
                    mt={{
                      xs: 2,
                      sm: 0,
                    }}
                  >
                    <SoftBox
                      mr={{
                        xs: 0,
                        sm: 2,
                      }}
                      sx={{
                        width: {
                          xs: "100%",
                          sm: "auto",
                        },
                      }}
                    >
                      <SoftInput
                        placeholder="Escribe aquí..."
                        icon={{ component: "search", direction: "left" }}
                        onChange={handleSearch}
                      />
                    </SoftBox>
                    <SoftBox
                      display={{
                        xs: "none",
                        sm: "block",
                      }}
                    >
                      <SoftBox display="flex" alignItems="center">
                        <SoftBox mr={1}>
                          <Tooltip title="Agregar nuevo evento" placement="top">
                            <SoftButton
                              color="primary"
                              circular
                              onClick={() => setOpenModalAdd(true)}
                            >
                              <Icon>event</Icon>
                              &nbsp;Agregar evento
                            </SoftButton>
                          </Tooltip>
                        </SoftBox>
                        <Tooltip title="Administrar clientes de eventos" placement="top">
                          <SoftButton
                            color="uproGreen"
                            circular
                            onClick={() => setOpenClientesAdmin(true)}
                          >
                            <Icon>group</Icon>
                            &nbsp;Administrar clientes
                          </SoftButton>
                        </Tooltip>
                      </SoftBox>
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
              </Grid>
            </Grid>
            <SoftBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <Loading />
              ) : allRows && allRows.length > 0 ? (
                <DataTable
                  columns={columns}
                  rows={allRows}
                  order={orderBy}
                  setOrder={handleOrderBy}
                />
              ) : (
                <SinDatos />
              )}
            </SoftBox>
            <CustomPagination
              loading={loading}
              length={data?.eventos.length}
              total={pagesInfo?.total}
              pages={pagesInfo?.pages}
              page={page}
              setPage={setPage}
              noCard
            />
          </Card>
          {renderMenu}
          <ModalAddEvento
            open={openModalAdd}
            handleClose={() => setOpenModalAdd(false)}
            refetch={refetch}
          />
          <DetallesEvento
            open={openModalDetalles}
            handleClose={() => setOpenModalDetalles(false)}
            selectedEvento={selectedItem}
          />
          <ClientesEvento open={openClientesAdmin} handleClose={() => setOpenClientesAdmin(false)} />
        </SoftBox>
      </SoftBox>
    </DashboardLayout>
  );
}

export default Eventos;
